<template>
  <el-dialog
    v-model="dialogVisible"
    title="我的粉丝"
    width="600px"
    :close-on-click-modal="false"
    :before-close="handleClose"
    class="warm-theme-dialog"
  >
    <div v-if="loading" class="loading">
      <el-skeleton :rows="4" animated />
    </div>
    <div v-else class="follower-list-container">
      <div v-if="followers.length === 0" class="empty-followers">
        暂无粉丝
      </div>
      <div v-else class="followers-list">
        <el-card 
          v-for="user in followers" 
          :key="user.id" 
          class="follower-item warm-card"
          shadow="never"
        >
          <div class="user-info" @click="showUserProfile(user.id)">
            <el-avatar :size="40" :src="user.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" class="warm-avatar" />
            <div class="user-details">
              <div class="username">{{ user.name || '未知用户' }}</div>
            </div>
          </div>
        </el-card>
        
        <!-- 分页 -->
        <div class="pagination-wrapper" v-if="total > 0">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :total="total"
            :page-sizes="[5, 10, 15, 20]"
            layout="total, sizes, prev, pager, next, jumper"
            class="warm-pagination"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </div>
    
    <!-- 用户信息弹窗 -->
    <OtherUserProfileModal
      v-model="otherUserProfileModalVisible"
      :userId="selectedUserId"
      @close="handleCloseModal"
      @report-user="handleReportUser"
    />
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getUserFollowings, getUserFollowers, followUser, unfollowUser } from '@/user/api/userApi';
import { useRouter } from 'vue-router';
import OtherUserProfileModal from '../user/OtherUserProfileModal.vue'; // 导入用户信息弹窗组件

interface Props {
  modelValue: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
  (e: 'report-user', payload: { targetType: number, targetId: number }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const router = useRouter();
const loading = ref(false);
const followers = ref<any[]>([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);
const otherUserProfileModalVisible = ref(false); // 控制用户信息弹窗显示
const selectedUserId = ref(0); // 当前选中的用户ID

// 显示用户信息弹窗
const showUserProfile = (userId: number) => {
  selectedUserId.value = userId;
  otherUserProfileModalVisible.value = true;
};

// 加载粉丝列表
const loadFollowers = async () => {
  try {
    loading.value = true;
    console.log('正在请求粉丝列表，参数:', { page: currentPage.value, pageSize: pageSize.value });
    
    const response = await getUserFollowers({
      page: currentPage.value,
      pageSize: pageSize.value
    });

    console.log('粉丝列表API响应:', response);
    
    // 检查API响应结构
    if ('total' in response.data && 'records' in response.data) {
      // 直接是分页数据结构
      followers.value = response.data.records || [];
      total.value = response.data.total || 0;
    } else {
      console.error('API返回错误格式:', response.data);
      throw new Error('获取粉丝列表失败');
    }
  } catch (error: any) {
    console.error('获取粉丝列表失败:', error);
    followers.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 分页改变
const handlePageChange = (page: number) => {
  currentPage.value = page;
  loadFollowers();
};

// 每页数量改变
const handleSizeChange = (size: number) => {
  pageSize.value = size;
  currentPage.value = 1;
  loadFollowers();
};

// 查看用户详情
const goToUserProfile = (userId: number) => {
  router.push(`/user/${userId}`);
  handleClose();
};

// 关闭对话框
const handleClose = () => {
  emit('close');
};

// 处理关闭用户资料弹窗
const handleCloseModal = () => {
  otherUserProfileModalVisible.value = false;
};

// 处理举报用户事件
const handleReportUser = (payload: { targetType: number, targetId: number }) => {
  // 将事件向上冒泡
  emit('report-user', payload);
};

// 监听弹窗显示状态变化
watch(dialogVisible, (newValue) => {
  if (newValue) {
    currentPage.value = 1;
    loadFollowers();
  }
});
</script>


<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&family=Quicksand:wght@400;500;600;700&display=swap');

.warm-theme-dialog {
  --bg-primary: #f9f5f0;
  --bg-secondary: #f0e6db;
  --accent-color: #d48341;
  --text-main: #5c4329;
  --text-light: #8b7d6b;
  --card-bg: #fffaf0;
  --border-radius: 12px;
  --shadow: 0 4px 12px rgba(139, 125, 107, 0.15);
  font-family: 'Nunito', 'Quicksand', 'Open Sans', sans-serif;
}

/* 添加编织纹理背景 */
.warm-theme-dialog :deep(.el-dialog__body) {
  background-image: 
    radial-gradient(circle at 15px 15px, #e6d5c3 2px, transparent 3px),
    radial-gradient(circle at calc(100% - 15px) 15px, #e6d5c3 2px, transparent 3px),
    radial-gradient(circle at 15px calc(100% - 15px), #e6d5c3 2px, transparent 3px),
    radial-gradient(circle at calc(100% - 15px) calc(100% - 15px), #e6d5c3 2px, transparent 3px);
  background-size: 60px 60px;
  background-position: 0 0;
  padding: 20px;
}

.loading {
  padding: 20px;
  text-align: center;
}

.follower-list-container {
  background: var(--bg-primary);
  padding: 15px;
  border-radius: var(--border-radius);
  position: relative;
  /* 添加钩针花边装饰 */
  background-image: 
    linear-gradient(90deg, transparent 45%, rgba(212, 131, 65, 0.1) 50%, transparent 55%),
    linear-gradient(180deg, transparent 45%, rgba(212, 131, 65, 0.1) 50%, transparent 55%);
  background-size: 40px 40px;
}

.followers-list {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden; /* 隐藏水平滚动条 */
  padding: 5px;
}

/* 隐藏滚动条 */
.followers-list::-webkit-scrollbar {
  display: none; /* 隐藏Webkit浏览器的滚动条 */
}

.followers-list {
  -ms-overflow-style: none;  /* IE和Edge隐藏滚动条 */
  scrollbar-width: none;  /* Firefox隐藏滚动条 */
}

.follower-item {
  margin-bottom: 12px;
  border-radius: var(--border-radius);
  padding: 15px;
  background: 
    linear-gradient(135deg, #fefcf7 0%, #fffaf0 100%),
    radial-gradient(at top left, rgba(212, 131, 65, 0.1) 0%, transparent 50%),
    radial-gradient(at bottom right, rgba(212, 131, 65, 0.1) 0%, transparent 50%);
  box-shadow: var(--shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: visible; /* 确保按钮显示 */
  border: 1px solid rgba(212, 131, 65, 0.3);
  border-radius: 15px;
  z-index: 1; /* 确保卡片在合适的层级 */
  box-sizing: border-box; /* 确保padding不影响总宽度 */
  width: 100%; /* 明确指定宽度 */
}

.follower-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #d48341, #e0a86a, #d48341);
  opacity: 0.8;
}

.follower-item::after {
  content: "●●●";
  position: absolute;
  bottom: 0;
  right: 10px;
  color: rgba(212, 131, 65, 0.2);
  font-size: 12px;
  letter-spacing: 2px;
}

.follower-item :deep(.el-card__body) {
  padding: 0 !important; /* 确保卡片内部没有额外的padding影响布局 */
  width: 100%; /* 确保卡片内容区占满宽度 */
  box-sizing: border-box; /* 防止padding导致溢出 */
}

.follower-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(212, 131, 65, 0.3);
}

.follower-item:last-child {
  margin-bottom: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;
  width: 100%;
  padding: 5px 0; /* 添加一些垂直间距 */
  cursor: pointer; /* 显示为可点击 */
  transition: background-color 0.3s ease; /* 添加过渡效果 */
  border-radius: 8px; /* 圆角 */
  padding: 8px; /* 内边距 */
  box-sizing: border-box; /* 确保padding不影响总宽度 */
  min-width: 0; /* 允许flex项目收缩 */
}

.user-info:hover {
  background-color: rgba(212, 131, 65, 0.1); /* 悬停时的背景色 */
}

.user-details {
  flex: 1;
  min-width: 0; /* 允许flex项目收缩到其内容的大小以下 */
  overflow: hidden;
}

.username {
  font-weight: 600;
  color: var(--text-main);
  font-family: 'Quicksand', 'Nunito', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0; /* 确保可以收缩 */
}


.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  padding: 15px 0;
  position: relative;
}

.warm-pagination :deep(.el-pager li),
.warm-pagination :deep(button),
.warm-pagination :deep(span) {
  font-family: 'Nunito', 'Open Sans', sans-serif;
  border-radius: 8px !important;
  margin: 0 4px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--bg-secondary);
}

.warm-pagination :deep(.el-pager li.is-active) {
  background-color: var(--accent-color) !important;
  color: white;
  border-color: var(--accent-color);
}

.empty-followers {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-light);
  font-size: 16px;
  font-style: italic;
  background: var(--card-bg);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  position: relative;
  border: 1px dashed var(--bg-secondary);
}

.empty-followers::before {
  content: "_hooks_";
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 24px;
  color: rgba(212, 131, 65, 0.2);
  font-family: "Segoe UI Emoji", sans-serif;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .warm-theme-dialog :deep(.el-dialog) {
    width: 95% !important;
    margin-top: 4vh;
    max-width: 600px;
  }
  
  .followers-list {
    max-height: 50vh;
  }
  
  .follower-item {
    padding: 12px;
  }
  
  .user-info {
    gap: 10px;
  }
  
  .pagination-wrapper {
    flex-wrap: wrap;
    row-gap: 10px;
  }
  
  .warm-pagination :deep(.el-pagination__total),
  .warm-pagination :deep(.el-pagination__sizes) {
    margin-bottom: 8px;
    display: flex;
    justify-content: center;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .warm-theme-dialog :deep(.el-dialog) {
    width: 98% !important;
    margin: 2vh 1%;
  }
  
  .warm-theme-dialog :deep(.el-dialog__body) {
    padding: 12px;
  }
  
  .follower-list-container {
    padding: 10px;
  }
  
  .follower-item {
    padding: 10px;
  }
  
  .user-info {
    gap: 8px;
    padding: 6px;
  }
  
  .username {
    font-size: 14px;
  }
  
  .warm-pagination :deep(.el-pager),
  .warm-pagination :deep(.btn-next),
  .warm-pagination :deep(.btn-prev) {
    margin: 0 1px;
  }
  
  .warm-pagination :deep(.el-input__inner) {
    padding: 2px 5px;
  }
}
</style>